import { useStanRouter } from '@/hooks/useStanRouter';
import { authApi, logWithSentry, MFA, removePrefixIfExists, validateMFACodeFormat } from '@/lib';
import { useAuthStore } from '@/stores';
import { useCallback, useState } from 'react';

export function useMFA(token: string, loginEndpoint: string, onSuccess?: () => void) {
    const [error, setError] = useState<{
        status: string;
        message: string;
        errors: Record<string, any>;
    } | null>(null);
    const [warning, setWarning] = useState('');
    const { push } = useStanRouter();
    const saveToken = useAuthStore(state => state.saveToken);

    const resetWarningsAndErrors = useCallback(() => {
        setError(null);
        setWarning('');
    }, []);

    const updateTokenAfterCodeVerification = (token: string) => {
        saveToken(token);
        onSuccess?.();
    };

    const validateConfigOTP = (code: string) => {
        resetWarningsAndErrors();
        let isFormOkay = true;
        if (!validateMFACodeFormat(code?.trim())) {
            setError(prev => ({
                status: prev?.status ?? '',
                message: prev?.message ?? '',
                errors: {
                    ...(prev?.errors || {}),
                    otp: ['Invalid code format. Please enter a valid 6-digit code.'],
                },
            }));
            isFormOkay = false;
        }

        if (isFormOkay) {
            if (!loginEndpoint) {
                logWithSentry('[MFA-VALIDATE-OTP] - no-login-endpoint', {
                    context: {
                        error: error,
                    },
                });
                push('/login');
                return;
            }
            const otpPayload = {
                totp: code?.trim(),
                ttl_days: MFA.DEFAULT_TTL_DAYS, // maximum ttl days
                ttl_start: Math.floor(Date.now() / 1000),
            };

            authApi
                .post(removePrefixIfExists(loginEndpoint, '/api/'), otpPayload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                .then(response => {
                    updateTokenAfterCodeVerification(response.data.token);
                })
                .catch(error => {
                    logWithSentry('[MFA-VALIDATE-OTP]', {
                        context: {
                            error: error,
                        },
                    });
                    if (error.response.status === 401) {
                        setError(prev => ({
                            status: prev?.status ?? '',
                            message: prev?.message ?? '',
                            errors: {
                                ...(prev?.errors || {}),
                                otp: ['Session expired. Please log in again'],
                            },
                        }));
                    } else if (error.response.status === 400) {
                        setError(prev => ({
                            status: prev?.status ?? '',
                            message: prev?.message ?? '',
                            errors: {
                                ...(prev?.errors || {}),
                                otp: ['Please enter a valid 6-digit code.'],
                            },
                        }));
                    }
                });
            // TODO: Add when recovery text input is added
            // .finally(() => {
            //     setIsLoading(false);
            // });
        }
    };
    return { validateConfigOTP, warning, error, resetWarningsAndErrors };
}
